import {
  AlertTriangle,
  BarChart3,
  Building,
  CalendarClock,
  ClipboardCheck,
  Coins,
  Download,
  Layers,
  Package,
  Printer,
  TrendingUp,
  Users,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PERMISSION } from '@obraplan/core/domain/access/permissions';
import { type BusinessDate } from '@obraplan/core/domain/date/business-date';
import { IsoWeek } from '@obraplan/core/domain/date/iso-week';
import { GRAIN_LABEL, type ReportGrain } from '@obraplan/core/domain/reports/hours-report';
import {
  completionOf,
  largestDeviations,
  periodProgress,
} from '@obraplan/core/domain/reports/indicators';
import { Hours } from '@obraplan/core/domain/shared/hours';
import { formatBusinessDate, formatShortDate } from '@/lib/format';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Section } from '@/components/ui/section';
import { SegmentedBar } from '@/components/ui/segmented-bar';
import { Field, Input } from '@/components/ui/input';
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/states';
import { AttentionList, type AttentionItem } from '@/features/reports/AttentionList';
import { ComparisonBars, TimeSeriesBars, type ChartSeries } from '@/features/reports/charts';
import { downloadCsv, toCsv } from '@/features/reports/csv';
import { Figure } from '@/features/reports/Figure';
import { kpisFor } from '@/features/reports/kpis';
import { KpiStrip } from '@/features/reports/KpiStrip';
import { PeriodRuler } from '@/features/reports/PeriodRuler';
import { useContainer, usePermission, useSessionState } from '@/features/session/use-session';
import { useLive } from '@/lib/use-live';

/**
 * Painel da obra (Q17, consolida Q01 a Q16). Todo número vem pronto do caso de uso — a
 * página não calcula horas, senão haveria duas contas de saldo divergindo. O período é
 * explícito no topo e viaja com o CSV exportado.
 *
 * A ordem da tela é a ordem das perguntas de quem chega: onde estamos no período; o que
 * exige ação; o plano contra o feito; a equipe; a conferência; as frentes e o custo.
 * Cada bloco tem um título de seção porque são perguntas diferentes, não cartões iguais.
 */

type PeriodPreset = 'WEEK' | 'MONTH' | 'LAST_30' | 'CUSTOM';

const PRESET_LABEL: Readonly<Record<PeriodPreset, string>> = {
  WEEK: 'Esta semana',
  MONTH: 'Este mês',
  LAST_30: 'Últimos 30 dias',
  CUSTOM: 'Personalizado',
};

function rangeFor(preset: PeriodPreset, today: BusinessDate): { from: string; to: string } {
  if (preset === 'WEEK') {
    const week = IsoWeek.of(today);
    return { from: week.startDate().toString(), to: week.endDate().toString() };
  }
  if (preset === 'MONTH') {
    return { from: today.startOfMonth().toString(), to: today.endOfMonth().toString() };
  }
  return { from: today.subtractDays(29).toString(), to: today.toString() };
}

const HOURS_SERIES: readonly ChartSeries[] = [
  { key: 'planned', label: 'Planejado', tone: 'planned' },
  { key: 'executed', label: 'Realizado', tone: 'executed' },
];

const CAPACITY_SERIES: readonly ChartSeries[] = [
  { key: 'available', label: 'Disponível', tone: 'available' },
  { key: 'planned', label: 'Planejado', tone: 'planned' },
  { key: 'executed', label: 'Realizado', tone: 'executed' },
];

const CHANGE_SERIES: readonly ChartSeries[] = [
  { key: 'total', label: 'Alterações', tone: 'attention' },
  { key: 'justified', label: 'Com justificativa', tone: 'planned' },
];

/** Quantas frentes a lista de maiores desvios mostra — o resto está na estrutura. */
const DEVIATION_LIMIT = 5;

function hoursOf(quarters: number): string {
  return Hours.ofQuarters(quarters).format();
}

function deviationNote(deltaQuarterHours: number, deltaPct: number | undefined): string {
  if (deltaQuarterHours === 0) return 'no plano';
  const signal = deltaQuarterHours > 0 ? '+' : '−';
  const absolute = hoursOf(Math.abs(deltaQuarterHours));
  return deltaPct === undefined
    ? `${signal}${absolute} sem plano`
    : `${signal}${absolute} (${signal}${Math.abs(deltaPct).toFixed(0)}%)`;
}

function pctOf(value: number | undefined): string {
  return value === undefined ? '—' : `${String(Math.round(value))}%`;
}

export function DashboardPage() {
  const container = useContainer();
  const { session, activeProjectId } = useSessionState();
  const canSeeReports = usePermission(PERMISSION.REPORTS_VIEW);
  const canSeeMaterials = usePermission(PERMISSION.REQUIREMENTS_VIEW);

  const today = useMemo(() => container.clock.today('America/Sao_Paulo'), [container]);
  const [preset, setPreset] = useState<PeriodPreset>('MONTH');
  const [custom, setCustom] = useState(() => rangeFor('MONTH', today));
  const [grainOverride, setGrainOverride] = useState<ReportGrain | undefined>(undefined);

  const range = preset === 'CUSTOM' ? custom : rangeFor(preset, today);

  const view = useLive(
    () =>
      session === undefined || activeProjectId === undefined
        ? Promise.resolve(undefined)
        : container.getDashboard.execute(
            {
              projectId: activeProjectId,
              from: range.from,
              to: range.to,
              ...(grainOverride === undefined ? {} : { grain: grainOverride }),
            },
            session,
          ),
    [session?.userId, activeProjectId, range.from, range.to, grainOverride],
  );

  const materials = useLive(
    () =>
      session === undefined || activeProjectId === undefined || !canSeeMaterials
        ? Promise.resolve(undefined)
        : container.getMaterialAlerts.execute(activeProjectId, session),
    [session?.userId, activeProjectId, canSeeMaterials],
  );

  if (activeProjectId === undefined) {
    return (
      <EmptyState
        title="Nenhuma obra selecionada"
        description="Escolha a obra no seletor acima para ver os indicadores."
        icon={<BarChart3 className="size-8" />}
      />
    );
  }

  if (!canSeeReports) {
    return (
      <EmptyState
        title="Sem acesso aos indicadores"
        description="Seu perfil não tem a permissão de relatórios nesta obra."
        icon={<BarChart3 className="size-8" />}
      />
    );
  }

  if (view === undefined) return <LoadingState label="Somando as horas do período…" />;
  if (!view.ok) return <ErrorState message={view.error.message} code={view.error.code} />;

  const data = view.value;
  const periodLabel = `${formatBusinessDate(data.from)} a ${formatBusinessDate(data.to)}`;
  const progress = periodProgress(data.from, data.to, data.today);
  const completion = completionOf(data.activities);
  const biggestDeviations = largestDeviations(data.byActivity, DEVIATION_LIMIT);

  const overloaded = data.byResource.filter((row) => row.totals.overloadQuarterHours > 0);
  // "Ocioso" é saldo relevante, não qualquer sobra: meia hora livre num mês não é
  // ociosidade, é arredondamento do calendário.
  const idle = data.byResource.filter((row) => row.totals.idleQuarterHours >= 4 * 4);
  const materialsOverdue = materials !== undefined && materials.ok ? materials.value.overdue : 0;
  const materialsDueSoon = materials !== undefined && materials.ok ? materials.value.dueSoon : 0;

  const attention: AttentionItem[] = [
    {
      key: 'overload',
      count: overloaded.length,
      title: overloaded.length === 1 ? 'recurso acima da jornada' : 'recursos acima da jornada',
      detail: `${hoursOf(data.totals.overloadQuarterHours)} planejadas além da capacidade`,
      quietLabel: 'ninguém acima da jornada',
      tone: 'danger',
      to: '/planejamento',
      linkLabel: 'Ver no planejamento semanal',
      icon: <Users className="size-4" aria-hidden="true" />,
    },
    {
      key: 'late',
      count: data.lateActivities.length,
      title:
        data.lateActivities.length === 1
          ? 'frente passou do fim previsto'
          : 'frentes passaram do fim previsto',
      detail: 'sem data de conclusão registrada',
      quietLabel: 'nenhuma frente atrasada',
      tone: 'danger',
      to: '/estrutura',
      linkLabel: 'Ver na estrutura da obra',
      icon: <AlertTriangle className="size-4" aria-hidden="true" />,
    },
    ...(canSeeMaterials
      ? [
          {
            key: 'materials',
            count: materialsOverdue,
            title: materialsOverdue === 1 ? 'material vencido' : 'materiais vencidos',
            detail:
              materials === undefined || !materials.ok
                ? 'aviso indisponível'
                : materialsDueSoon > 0
                  ? `sem confirmação · mais ${String(materialsDueSoon)} vencem no horizonte da obra`
                  : 'sem confirmação',
            quietLabel:
              materialsDueSoon > 0
                ? `nenhum material vencido (${String(materialsDueSoon)} vencem em breve)`
                : 'nenhum material vencido',
            tone: 'danger' as const,
            to: '/cronograma',
            linkLabel: 'Ver no cronograma',
            icon: <Package className="size-4" aria-hidden="true" />,
          },
        ]
      : []),
    {
      key: 'idle',
      count: idle.length,
      title: idle.length === 1 ? 'recurso com saldo parado' : 'recursos com saldo parado',
      detail: `${hoursOf(data.totals.idleQuarterHours)} disponíveis sem plano`,
      quietLabel: 'ninguém ocioso',
      tone: 'warn',
      to: '/planejamento',
      linkLabel: 'Ver no planejamento semanal',
      icon: <CalendarClock className="size-4" aria-hidden="true" />,
    },
  ];

  const exportHours = (): void => {
    const csv = toCsv(
      [
        'Período',
        'Início',
        'Fim',
        'Disponível (h)',
        'Planejado (h)',
        'Realizado (h)',
        'Saldo (h)',
        'Ociosidade (h)',
      ],
      data.byPeriod.map((bucket) => [
        bucket.label,
        formatBusinessDate(bucket.from),
        formatBusinessDate(bucket.to),
        bucket.totals.availableQuarterHours / 4,
        bucket.totals.plannedQuarterHours / 4,
        bucket.totals.executedQuarterHours / 4,
        bucket.totals.balanceQuarterHours / 4,
        bucket.totals.idleQuarterHours / 4,
      ]),
    );
    downloadCsv(`horas-${data.from}-a-${data.to}`, csv);
  };

  const exportResources = (): void => {
    const csv = toCsv(
      [
        'Recurso',
        'Detalhe',
        'Equipe',
        'Disponível (h)',
        'Planejado (h)',
        'Realizado (h)',
        'Saldo (h)',
      ],
      data.byResource.map((row) => [
        row.resource.name,
        row.resource.detail,
        row.resource.teamName ?? '',
        row.totals.availableQuarterHours / 4,
        row.totals.plannedQuarterHours / 4,
        row.totals.executedQuarterHours / 4,
        row.totals.balanceQuarterHours / 4,
      ]),
    );
    downloadCsv(`recursos-${data.from}-a-${data.to}`, csv);
  };

  const exportDisciplines = (): void => {
    const csv = toCsv(
      [
        'Disciplina',
        'Planejado (h)',
        'Realizado (h)',
        'Desvio (h)',
        'Desvio (%)',
        'Dias por conferir',
      ],
      data.byDiscipline.map((row) => [
        row.label,
        row.deviation.plannedQuarterHours / 4,
        row.deviation.executedQuarterHours / 4,
        row.deviation.deltaQuarterHours / 4,
        row.deviation.deltaPct === undefined ? '' : row.deviation.deltaPct,
        row.pendingCells,
      ]),
    );
    downloadCsv(`planejado-x-realizado-${data.from}-a-${data.to}`, csv);
  };

  const deviationRows = (groups: typeof data.byDiscipline) =>
    groups.map((row) => ({
      key: row.key,
      label: row.label,
      values: [row.deviation.plannedQuarterHours, row.deviation.executedQuarterHours],
      note:
        row.pendingCells > 0
          ? `${String(row.pendingCells)} por conferir`
          : deviationNote(row.deviation.deltaQuarterHours, row.deviation.deltaPct),
      noteTone:
        row.pendingCells > 0
          ? ('muted' as const)
          : row.deviation.deltaQuarterHours === 0
            ? ('ok' as const)
            : ('warn' as const),
    }));

  return (
    <div className="flex flex-col gap-8">
      {/* ── Cabeçalho: o período é a primeira coisa, e a régua o desenha ─────── */}
      <header className="flex flex-col gap-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold">Painel da obra</h1>
            <p className="text-(--color-content-muted)">
              {periodLabel} · agrupado por {GRAIN_LABEL[data.grain].toLowerCase()}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 print:hidden">
            <Button variant="secondary" onClick={exportHours}>
              <Download aria-hidden="true" />
              Exportar horas
            </Button>
            <Button variant="secondary" onClick={() => globalThis.print()}>
              <Printer aria-hidden="true" />
              Imprimir
            </Button>
          </div>
        </div>

        <Card>
          <CardContent className="flex flex-col gap-4 p-4 md:p-5">
            <PeriodRuler
              from={data.from}
              to={data.to}
              today={data.today}
              buckets={data.byPeriod}
              progress={progress}
              inspectedCells={data.totals.inspectedCells}
              totalCells={data.totals.totalCells}
            />

            <div className="flex flex-wrap items-end gap-x-4 gap-y-3 border-t border-(--color-border-subtle) pt-4 print:hidden">
              <div className="flex flex-wrap gap-1" role="group" aria-label="Período">
                {(Object.keys(PRESET_LABEL) as PeriodPreset[]).map((option) => (
                  <Button
                    key={option}
                    size="sm"
                    variant={preset === option ? 'primary' : 'ghost'}
                    onClick={() => {
                      if (option === 'CUSTOM') setCustom(range);
                      setPreset(option);
                    }}
                  >
                    {PRESET_LABEL[option]}
                  </Button>
                ))}
              </div>

              {preset === 'CUSTOM' && (
                <>
                  <Field label="De" htmlFor="dashboard-from">
                    <Input
                      id="dashboard-from"
                      type="date"
                      className="w-40"
                      value={custom.from}
                      onChange={(event) =>
                        setCustom((prev) => ({ ...prev, from: event.target.value }))
                      }
                    />
                  </Field>
                  <Field label="Até" htmlFor="dashboard-to">
                    <Input
                      id="dashboard-to"
                      type="date"
                      className="w-40"
                      value={custom.to}
                      onChange={(event) =>
                        setCustom((prev) => ({ ...prev, to: event.target.value }))
                      }
                    />
                  </Field>
                </>
              )}

              <div className="ml-auto">
                <Field label="Agrupar por" htmlFor="dashboard-grain">
                  <select
                    id="dashboard-grain"
                    className="h-11 rounded-md border border-(--color-border-subtle) bg-(--color-surface) px-2 text-sm md:h-8"
                    value={grainOverride ?? 'AUTO'}
                    onChange={(event) =>
                      setGrainOverride(
                        event.target.value === 'AUTO'
                          ? undefined
                          : (event.target.value as ReportGrain),
                      )
                    }
                  >
                    <option value="AUTO">Automático</option>
                    <option value="DAY">Dia</option>
                    <option value="WEEK">Semana</option>
                    <option value="MONTH">Mês</option>
                  </select>
                </Field>
              </div>
            </div>
          </CardContent>
        </Card>
      </header>

      <KpiStrip items={kpisFor(data.totals)} />

      {/* ── O que exige ação ─────────────────────────────────────────────────── */}
      <Section title="Precisa de atenção">
        <AttentionList items={attention} />
      </Section>

      {/* ── Planejado × realizado ────────────────────────────────────────────── */}
      <Section
        title="Planejado × realizado"
        description="O realizado só existe onde a conferência já passou; o planejado é reconstruído do histórico de alteração."
      >
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="size-4" aria-hidden="true" />
                No tempo
              </CardTitle>
              <CardDescription>
                Por {GRAIN_LABEL[data.grain].toLowerCase()}, ao longo do período.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <TimeSeriesBars
                title={`Planejado e realizado por ${GRAIN_LABEL[data.grain].toLowerCase()}`}
                series={HOURS_SERIES}
                points={data.byPeriod.map((bucket) => ({
                  key: bucket.key,
                  label: bucket.label,
                  values: [bucket.totals.plannedQuarterHours, bucket.totals.executedQuarterHours],
                }))}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex-row items-start justify-between gap-3">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Layers className="size-4" aria-hidden="true" />
                  Por disciplina
                </CardTitle>
                <CardDescription>Onde o plano se cumpre e onde ele escapa.</CardDescription>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={exportDisciplines}
                className="print:hidden"
                aria-label="Exportar planejado × realizado por disciplina"
              >
                <Download aria-hidden="true" />
              </Button>
            </CardHeader>
            <CardContent>
              <ComparisonBars
                title="Planejado e realizado por disciplina"
                series={HOURS_SERIES}
                rows={deviationRows(data.byDiscipline)}
                emptyMessage="Nenhuma frente teve hora lançada no período."
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="size-4" aria-hidden="true" />
                Por pavimento
              </CardTitle>
              <CardDescription>O mesmo recorte, andar por andar.</CardDescription>
            </CardHeader>
            <CardContent>
              <ComparisonBars
                title="Planejado e realizado por pavimento"
                series={HOURS_SERIES}
                rows={deviationRows(data.byFloor)}
                emptyMessage="Nenhum pavimento teve hora lançada no período."
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="size-4" aria-hidden="true" />
                Frentes que mais fugiram do plano
              </CardTitle>
              <CardDescription>
                As {DEVIATION_LIMIT} de maior diferença entre planejado e realizado, para cima ou
                para baixo.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              {biggestDeviations.length === 0 ? (
                <p className="text-sm text-(--color-content-muted)">
                  Nenhuma frente fugiu do plano nos dias já conferidos.
                </p>
              ) : (
                <ol className="flex flex-col divide-y divide-(--color-border-subtle)">
                  {biggestDeviations.map((row) => {
                    const behind = row.deviation.deltaQuarterHours < 0;
                    return (
                      <li
                        key={row.key}
                        className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 py-2 first:pt-0 last:pb-0"
                      >
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium">{row.label}</span>
                          <span className="block text-xs text-(--color-content-muted)">
                            {hoursOf(row.deviation.plannedQuarterHours)} planejadas ·{' '}
                            {hoursOf(row.deviation.executedQuarterHours)} realizadas
                            {row.pendingCells > 0 &&
                              ` · ${String(row.pendingCells)} dias por conferir`}
                          </span>
                        </span>
                        <span
                          className={cn(
                            'tabular text-sm font-semibold',
                            behind ? 'text-(--color-state-warn)' : 'text-(--color-state-info)',
                          )}
                        >
                          {deviationNote(row.deviation.deltaQuarterHours, row.deviation.deltaPct)}
                        </span>
                      </li>
                    );
                  })}
                </ol>
              )}
              <Link
                to="/estrutura"
                className="text-sm text-(--color-brand) underline-offset-4 hover:underline print:hidden"
              >
                Ver todas as frentes na estrutura
              </Link>
            </CardContent>
          </Card>
        </div>
      </Section>

      {/* ── Equipe ───────────────────────────────────────────────────────────── */}
      <Section title="Equipe">
        <Card>
          <CardHeader className="flex-row items-start justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="size-4" aria-hidden="true" />
                Capacidade por recurso
              </CardTitle>
              <CardDescription>
                Disponível somado dia a dia pelo calendário — nunca jornada × número de dias.
              </CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={exportResources} className="print:hidden">
              <Download aria-hidden="true" />
              Exportar
            </Button>
          </CardHeader>
          <CardContent>
            <ComparisonBars
              title="Disponível, planejado e realizado por recurso"
              series={CAPACITY_SERIES}
              rows={data.byResource.slice(0, 20).map((row) => ({
                key: row.resource.resourceId,
                label: `${row.resource.name} · ${row.resource.detail}`,
                values: [
                  row.totals.availableQuarterHours,
                  row.totals.plannedQuarterHours,
                  row.totals.executedQuarterHours,
                ],
                note:
                  row.totals.overloadQuarterHours > 0
                    ? `sobrecarga de ${hoursOf(row.totals.overloadQuarterHours)}`
                    : row.totals.idleQuarterHours > 0
                      ? `${hoursOf(row.totals.idleQuarterHours)} livres`
                      : undefined,
                noteTone: row.totals.overloadQuarterHours > 0 ? 'danger' : 'muted',
              }))}
              emptyMessage="Nenhum recurso com capacidade ou plano no período."
            />
            {data.byResource.length > 20 && (
              <p className="mt-3 text-xs text-(--color-content-muted)">
                Mostrando os 20 com mais horas planejadas; o CSV traz os {data.byResource.length}.
              </p>
            )}
          </CardContent>
        </Card>
      </Section>

      {/* ── Conferência e alterações ─────────────────────────────────────────── */}
      <Section title="Conferência e alterações">
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ClipboardCheck className="size-4" aria-hidden="true" />
                Conferência do período
              </CardTitle>
              <CardDescription>Sobre os dias-recurso que tinham planejamento.</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <div className="flex items-baseline gap-2">
                <Figure value={pctOf(data.inspections.ratePct)} size="xl" />
                <span className="text-sm text-(--color-content-muted)">
                  dos {data.inspections.plannedCells} dias-recurso planejados já foram conferidos
                </span>
              </div>
              <SegmentedBar
                title="Situação da conferência"
                segments={[
                  {
                    key: 'confirmed',
                    label: 'confirmadas',
                    value: data.inspections.confirmed,
                    tone: 'ok',
                  },
                  {
                    key: 'adjusted',
                    label: 'ajustadas',
                    value: data.inspections.adjusted,
                    tone: 'info',
                  },
                  {
                    key: 'absent',
                    label: 'ausências',
                    value: data.inspections.absent,
                    tone: 'warn',
                  },
                  {
                    key: 'pending',
                    label: 'pendentes',
                    value: data.inspections.pending,
                    tone: 'neutral',
                  },
                ]}
              />
              <Link
                to="/conferencia"
                className="text-sm text-(--color-brand) underline-offset-4 hover:underline print:hidden"
              >
                Abrir a conferência diária
              </Link>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="size-4" aria-hidden="true" />
                Alterações de planejamento
              </CardTitle>
              <CardDescription>
                {data.changes.total} no período, {data.changes.withJustification} com justificativa
                (hora extra, RN-292).
              </CardDescription>
            </CardHeader>
            <CardContent>
              <TimeSeriesBars
                title="Alterações de planejamento por dia"
                series={CHANGE_SERIES}
                format={(value) => String(Math.round(value))}
                points={data.changes.byDay.map((day) => ({
                  key: day.date,
                  label: formatShortDate(day.date),
                  values: [day.total, day.withJustification],
                }))}
              />
              {data.mostChangedActivities.length > 0 && (
                <div className="mt-4">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-(--color-content-muted)">
                    Frentes mais alteradas
                  </p>
                  <ul className="mt-1.5 flex flex-col divide-y divide-(--color-border-subtle) text-sm">
                    {data.mostChangedActivities.slice(0, 5).map((item) => (
                      <li key={item.label} className="flex justify-between gap-3 py-1.5">
                        <span className="truncate">{item.label}</span>
                        <span className="tabular text-(--color-content-muted)">{item.count}×</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </Section>

      {/* ── Frentes e custo ──────────────────────────────────────────────────── */}
      <Section title="Frentes e custo">
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Layers className="size-4" aria-hidden="true" />
                Frentes da obra
              </CardTitle>
              <CardDescription>
                {data.activities.total} no total · {data.lateActivities.length} passaram do fim
                previsto
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <div className="flex items-baseline gap-2">
                  <Figure value={pctOf(completion.pct)} size="xl" />
                  <span className="text-sm text-(--color-content-muted)">
                    concluídas · {completion.done} de {completion.considered}
                  </span>
                </div>
                <div
                  className="h-1.5 w-full overflow-hidden rounded-full bg-(--color-surface-muted)"
                  role="meter"
                  aria-valuenow={completion.done}
                  aria-valuemin={0}
                  aria-valuemax={completion.considered}
                  aria-label={`${String(completion.done)} de ${String(completion.considered)} frentes concluídas`}
                >
                  <div
                    className="h-full rounded-full bg-(--color-state-ok)"
                    style={{ width: `${String(completion.pct ?? 0)}%` }}
                  />
                </div>
              </div>
              <ul className="flex flex-col divide-y divide-(--color-border-subtle) text-sm">
                {data.activities.byStatus.map((item) => (
                  <li key={item.status} className="flex justify-between gap-3 py-1.5">
                    <span>{item.label}</span>
                    <span className="tabular font-medium">{item.count}</span>
                  </li>
                ))}
              </ul>
              {data.lateActivities.length > 0 && (
                <details>
                  <summary className="cursor-pointer text-xs text-(--color-state-danger)">
                    Ver as {data.lateActivities.length} frentes atrasadas
                  </summary>
                  <ul className="mt-2 flex flex-col gap-1 text-sm">
                    {data.lateActivities.map((activity) => (
                      <li key={activity.activityId} className="flex justify-between gap-3">
                        <span className="truncate">{activity.label}</span>
                        <span className="text-xs text-(--color-content-muted)">
                          {activity.towerName}
                        </span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Coins className="size-4" aria-hidden="true" />
                Custo da mão de obra
              </CardTitle>
              <CardDescription>
                Funcionário: salário ÷ horas úteis do mês; terceirizado, por hora ou por unidade.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <dl className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1">
                  <dt className="text-[11px] font-semibold uppercase tracking-[0.08em] text-(--color-content-muted)">
                    Planejado
                  </dt>
                  <dd>
                    <Figure value={data.cost.planned.format()} size="md" />
                  </dd>
                </div>
                <div className="flex flex-col gap-1">
                  <dt className="text-[11px] font-semibold uppercase tracking-[0.08em] text-(--color-content-muted)">
                    Realizado
                  </dt>
                  <dd>
                    <Figure value={data.cost.executed.format()} size="md" />
                  </dd>
                </div>
              </dl>
              {/* Serviço cobrado por m² não tem hora que o explique: sem esta linha, o total
                  subiria sem que nada dissesse de onde veio. */}
              {!data.cost.unitPricedPlanned.isZero() && (
                <p className="text-sm text-(--color-content-muted)">
                  Inclui {data.cost.unitPricedPlanned.format()} planejados e{' '}
                  {data.cost.unitPricedExecuted.format()} realizados em serviços cobrados por
                  produção.
                </p>
              )}
              {data.cost.resourcesWithoutRate > 0 && (
                <p className="text-sm text-(--color-state-warn)">
                  Total parcial: {data.cost.resourcesWithoutRate} recurso(s) sem preço cadastrado
                  ficam de fora. Custo indisponível não é custo zero.
                </p>
              )}
              {data.cost.coveredResources === 0 && (
                <p className="text-sm text-(--color-content-muted)">
                  Nenhum recurso do período tem salário ou preço cadastrado.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </Section>
    </div>
  );
}
